import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import dayjs from 'dayjs';
import CryptoJS from 'crypto-js';
import { JSEncrypt } from 'jsencrypt';
import { isString, isObject } from '@vue/shared';
export { isFunction, isObject, isString } from '@vue/shared';
import { createDefu } from 'defu';
export { createDefu as createMerge, defu as merge } from 'defu';
export { default as cloneDeep } from 'lodash.clonedeep';
export { default as get } from 'lodash.get';
export { default as isEqual } from 'lodash.isequal';
export { default as set } from 'lodash.set';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

function formatDate(time, format = "YYYY-MM-DD") {
  if (!time) {
    return time;
  }
  try {
    const date = dayjs(time);
    if (!date.isValid()) {
      throw new Error("Invalid date");
    }
    return date.format(format);
  } catch (error) {
    console.error(`Error formatting date: ${error}`);
    return time;
  }
}
function formatDateTime(time) {
  if (!time) {
    return time;
  }
  return formatDate(time, "YYYY-MM-DD HH:mm:ss");
}
function formatDate2(date, format) {
  if (!date) {
    return "";
  }
  return date ? dayjs(date).format(format ?? "YYYY-MM-DD HH:mm:ss") : "";
}
function isDate(value) {
  return value instanceof Date;
}
function isDayjsObject(value) {
  return dayjs.isDayjs(value);
}
function dateFormatter(_row, _column, cellValue) {
  return cellValue ? formatDate(cellValue)?.toString() || "" : "";
}

function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  const counter = /* @__PURE__ */ new Map();
  for (const value of a) {
    counter.set(value, (counter.get(value) || 0) + 1);
  }
  for (const value of b) {
    const count = counter.get(value);
    if (count === void 0 || count === 0) {
      return false;
    }
    counter.set(value, count - 1);
  }
  return true;
}
function diff(obj1, obj2) {
  function findDifferences(o1, o2) {
    if (Array.isArray(o1) && Array.isArray(o2)) {
      if (!arraysEqual(o1, o2)) {
        return o2;
      }
      return void 0;
    }
    if (typeof o1 === "object" && typeof o2 === "object" && o1 !== null && o2 !== null) {
      const diffResult = {};
      const keys = /* @__PURE__ */ new Set([...Object.keys(o1), ...Object.keys(o2)]);
      keys.forEach((key) => {
        const valueDiff = findDifferences(o1[key], o2[key]);
        if (valueDiff !== void 0) {
          diffResult[key] = valueDiff;
        }
      });
      return Object.keys(diffResult).length > 0 ? diffResult : void 0;
    }
    return o1 === o2 ? void 0 : o2;
  }
  return findDifferences(obj1, obj2);
}

function getElementVisibleRect(element) {
  if (!element) {
    return {
      bottom: 0,
      height: 0,
      left: 0,
      right: 0,
      top: 0,
      width: 0
    };
  }
  const rect = element.getBoundingClientRect();
  const viewHeight = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight
  );
  const top = Math.max(rect.top, 0);
  const bottom = Math.min(rect.bottom, viewHeight);
  const viewWidth = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth
  );
  const left = Math.max(rect.left, 0);
  const right = Math.min(rect.right, viewWidth);
  return {
    bottom,
    height: Math.max(0, bottom - top),
    left,
    right,
    top,
    width: Math.max(0, right - left)
  };
}
function getScrollbarWidth() {
  const scrollDiv = document.createElement("div");
  scrollDiv.style.visibility = "hidden";
  scrollDiv.style.overflow = "scroll";
  scrollDiv.style.position = "absolute";
  scrollDiv.style.top = "-9999px";
  document.body.append(scrollDiv);
  const innerDiv = document.createElement("div");
  scrollDiv.append(innerDiv);
  const scrollbarWidth = scrollDiv.offsetWidth - innerDiv.offsetWidth;
  scrollDiv.remove();
  return scrollbarWidth;
}
function needsScrollbar() {
  const doc = document.documentElement;
  const body = document.body;
  const overflowY = window.getComputedStyle(body).overflowY;
  if (overflowY === "scroll" || overflowY === "auto") {
    return doc.scrollHeight > window.innerHeight;
  }
  return doc.scrollHeight > window.innerHeight;
}
function triggerWindowResize() {
  const resizeEvent = new Event("resize");
  window.dispatchEvent(resizeEvent);
}

function openWindow(url, options = {}) {
  const { noopener = true, noreferrer = true, target = "_blank" } = options;
  const features = [noopener && "noopener=yes", noreferrer && "noreferrer=yes"].filter(Boolean).join(",");
  window.open(url, target, features);
}
function openRouteInNewWindow(path) {
  const { hash, origin } = location;
  const fullPath = path.startsWith("/") ? path : `/${path}`;
  const url = `${origin}${hash && !fullPath.startsWith("/#") ? "/#" : ""}${fullPath}`;
  openWindow(url, { target: "_blank" });
}

const DEFAULT_FILENAME = "downloaded_file";
async function downloadFileFromUrl({
  fileName,
  source,
  target = "_blank"
}) {
  if (!source || typeof source !== "string") {
    throw new Error("Invalid URL.");
  }
  const isChrome = window.navigator.userAgent.toLowerCase().includes("chrome");
  const isSafari = window.navigator.userAgent.toLowerCase().includes("safari");
  if (/iP/.test(window.navigator.userAgent)) {
    console.error("Your browser does not support download!");
    return;
  }
  if (isChrome || isSafari) {
    triggerDownload(source, resolveFileName(source, fileName));
    return;
  }
  if (!source.includes("?")) {
    source += "?download";
  }
  openWindow(source, { target });
}
function downloadImageByCanvas({
  url,
  canvasWidth,
  canvasHeight,
  drawWithImageSize = true
}) {
  const image = new Image();
  image.src = url;
  image.addEventListener("load", () => {
    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth || image.width;
    canvas.height = canvasHeight || image.height;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    if (drawWithImageSize) {
      ctx.drawImage(image, 0, 0, image.width, image.height);
    } else {
      ctx.drawImage(image, 0, 0);
    }
    const url2 = canvas.toDataURL("image/png");
    downloadFileFromImageUrl({ source: url2, fileName: "image.png" });
  });
}
function downloadFileFromBase64({ fileName, source }) {
  if (!source || typeof source !== "string") {
    throw new Error("Invalid Base64 data.");
  }
  const resolvedFileName = fileName || DEFAULT_FILENAME;
  triggerDownload(source, resolvedFileName);
}
async function downloadFileFromImageUrl({
  fileName,
  source
}) {
  const base64 = await urlToBase64(source);
  downloadFileFromBase64({ fileName, source: base64 });
}
function downloadFileFromBlob({
  fileName = DEFAULT_FILENAME,
  source
}) {
  if (!(source instanceof Blob)) {
    throw new TypeError("Invalid Blob data.");
  }
  const url = URL.createObjectURL(source);
  triggerDownload(url, fileName);
}
function downloadFileFromBlobPart({
  fileName = DEFAULT_FILENAME,
  source
}) {
  const blob = source instanceof Blob ? source : new Blob([source], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, fileName);
}
function dataURLtoBlob(base64Buf) {
  const arr = base64Buf.split(",");
  const typeItem = arr[0];
  const mime = typeItem.match(/:(.*?);/)[1];
  const bstr = window.atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.codePointAt(n);
  }
  return new Blob([u8arr], { type: mime });
}
function urlToBase64(url, mineType) {
  return new Promise((resolve, reject) => {
    let canvas = document.createElement("CANVAS");
    const ctx = canvas?.getContext("2d");
    const img = new Image();
    img.crossOrigin = "";
    img.addEventListener("load", () => {
      if (!canvas || !ctx) {
        return reject(new Error("Failed to create canvas."));
      }
      canvas.height = img.height;
      canvas.width = img.width;
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL(mineType || "image/png");
      canvas = null;
      resolve(dataURL);
    });
    img.src = url;
  });
}
function base64ToFile(base64, fileName) {
  if (!base64 || typeof base64 !== "string") {
    throw new Error("base64 \u53C2\u6570\u5FC5\u987B\u662F\u975E\u7A7A\u5B57\u7B26\u4E32");
  }
  const data = base64.split(",");
  if (data.length !== 2 || !data[0] || !data[1]) {
    throw new Error("\u65E0\u6548\u7684 base64 \u683C\u5F0F");
  }
  const typeMatch = data[0].match(/:(.*?);/);
  if (!typeMatch || !typeMatch[1]) {
    throw new Error("\u65E0\u6CD5\u89E3\u6790 base64 \u7C7B\u578B\u4FE1\u606F");
  }
  const type = typeMatch[1];
  const typeParts = type.split("/");
  if (typeParts.length !== 2 || !typeParts[1]) {
    throw new Error("\u65E0\u6548\u7684 MIME \u7C7B\u578B\u683C\u5F0F");
  }
  const suffix = typeParts[1];
  try {
    const bstr = window.atob(data[1]);
    const n = bstr.length;
    const u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new File([u8arr], `${fileName}.${suffix}`, { type });
  } catch (error) {
    throw new Error(
      `Base64 \u89E3\u7801\u5931\u8D25: ${error instanceof Error ? error.message : "\u672A\u77E5\u9519\u8BEF"}`
    );
  }
}
function triggerDownload(href, fileName, revokeDelay = 100) {
  const defaultFileName = "downloaded_file";
  const finalFileName = fileName || defaultFileName;
  const link = document.createElement("a");
  link.href = href;
  link.download = finalFileName;
  link.style.display = "none";
  if (link.download === void 0) {
    link.setAttribute("target", "_blank");
  }
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), revokeDelay);
}
function resolveFileName(url, fileName) {
  return fileName || url.slice(url.lastIndexOf("/") + 1) || DEFAULT_FILENAME;
}

const AES = {
  /**
   * AES 加密
   * @param data 要加密的数据
   * @param key 加密密钥
   * @returns 加密后的字符串
   */
  encrypt(data, key) {
    try {
      if (!key) {
        throw new Error("AES \u52A0\u5BC6\u5BC6\u94A5\u4E0D\u80FD\u4E3A\u7A7A");
      }
      if (key.length !== 32 && key.length !== 16) {
        throw new Error(
          `AES \u52A0\u5BC6\u5BC6\u94A5\u957F\u5EA6\u5FC5\u987B\u4E3A 32 \u4F4D\u6216 16 \u4F4D\uFF0C\u5F53\u524D\u957F\u5EA6: ${key.length}`
        );
      }
      const keyUtf8 = CryptoJS.enc.Utf8.parse(key);
      const encrypted = CryptoJS.AES.encrypt(data, keyUtf8, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      });
      return encrypted.toString();
    } catch (error) {
      console.error("AES \u52A0\u5BC6\u5931\u8D25:", error);
      throw error;
    }
  },
  /**
   * AES 解密
   * @param encryptedData 加密的数据
   * @param key 解密密钥
   * @returns 解密后的字符串
   */
  decrypt(encryptedData, key) {
    try {
      if (!key) {
        throw new Error("AES \u89E3\u5BC6\u5BC6\u94A5\u4E0D\u80FD\u4E3A\u7A7A");
      }
      if (key.length !== 32) {
        throw new Error(
          `AES \u89E3\u5BC6\u5BC6\u94A5\u957F\u5EA6\u5FC5\u987B\u4E3A 32 \u4F4D\uFF0C\u5F53\u524D\u957F\u5EA6: ${key.length}`
        );
      }
      if (!encryptedData) {
        throw new Error("AES \u89E3\u5BC6\u6570\u636E\u4E0D\u80FD\u4E3A\u7A7A");
      }
      const keyUtf8 = CryptoJS.enc.Utf8.parse(key);
      const decrypted = CryptoJS.AES.decrypt(encryptedData, keyUtf8, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      });
      const result = decrypted.toString(CryptoJS.enc.Utf8);
      if (!result) {
        throw new Error("AES \u89E3\u5BC6\u7ED3\u679C\u4E3A\u7A7A\uFF0C\u53EF\u80FD\u662F\u5BC6\u94A5\u9519\u8BEF\u6216\u6570\u636E\u635F\u574F");
      }
      return result;
    } catch (error) {
      console.error("AES \u89E3\u5BC6\u5931\u8D25:", error);
      throw error;
    }
  }
};
const RSA = {
  /**
   * RSA 加密
   * @param data 要加密的数据
   * @param publicKey 公钥（必需）
   * @returns 加密后的字符串
   */
  encrypt(data, publicKey) {
    try {
      if (!publicKey) {
        throw new Error("RSA \u516C\u94A5\u4E0D\u80FD\u4E3A\u7A7A");
      }
      const encryptor = new JSEncrypt();
      encryptor.setPublicKey(publicKey);
      const result = encryptor.encrypt(data);
      if (result === false) {
        throw new Error("RSA \u52A0\u5BC6\u5931\u8D25\uFF0C\u53EF\u80FD\u662F\u516C\u94A5\u683C\u5F0F\u9519\u8BEF\u6216\u6570\u636E\u8FC7\u957F");
      }
      return result;
    } catch (error) {
      console.error("RSA \u52A0\u5BC6\u5931\u8D25:", error);
      throw error;
    }
  },
  /**
   * RSA 解密
   * @param encryptedData 加密的数据
   * @param privateKey 私钥（必需）
   * @returns 解密后的字符串
   */
  decrypt(encryptedData, privateKey) {
    try {
      if (!privateKey) {
        throw new Error("RSA \u79C1\u94A5\u4E0D\u80FD\u4E3A\u7A7A");
      }
      if (!encryptedData) {
        throw new Error("RSA \u89E3\u5BC6\u6570\u636E\u4E0D\u80FD\u4E3A\u7A7A");
      }
      const encryptor = new JSEncrypt();
      encryptor.setPrivateKey(privateKey);
      const result = encryptor.decrypt(encryptedData);
      if (result === false) {
        throw new Error("RSA \u89E3\u5BC6\u5931\u8D25\uFF0C\u53EF\u80FD\u662F\u79C1\u94A5\u9519\u8BEF\u6216\u6570\u636E\u635F\u574F");
      }
      return result;
    } catch (error) {
      console.error("RSA \u89E3\u5BC6\u5931\u8D25:", error);
      throw error;
    }
  }
};
class ApiEncrypt {
  config;
  constructor(config) {
    this.config = config;
  }
  /**
   * 解密响应数据
   * @param encryptedData 加密的响应数据
   * @returns 解密后的数据
   */
  decryptResponse(encryptedData) {
    if (!this.config.enable) {
      return encryptedData;
    }
    try {
      let decryptedData = "";
      if (this.config.algorithm.toUpperCase() === "AES") {
        if (!this.config.responseKey) {
          throw new Error("AES \u54CD\u5E94\u89E3\u5BC6\u5BC6\u94A5\u672A\u914D\u7F6E");
        }
        decryptedData = AES.decrypt(encryptedData, this.config.responseKey);
      } else if (this.config.algorithm.toUpperCase() === "RSA") {
        if (!this.config.responseKey) {
          throw new Error("RSA \u79C1\u94A5\u672A\u914D\u7F6E");
        }
        decryptedData = RSA.decrypt(encryptedData, this.config.responseKey);
        if (decryptedData === false) {
          throw new Error("RSA \u89E3\u5BC6\u5931\u8D25");
        }
      } else {
        throw new Error(`\u4E0D\u652F\u6301\u7684\u89E3\u5BC6\u7B97\u6CD5: ${this.config.algorithm}`);
      }
      if (!decryptedData) {
        throw new Error("\u89E3\u5BC6\u7ED3\u679C\u4E3A\u7A7A");
      }
      try {
        return JSON.parse(decryptedData);
      } catch {
        return decryptedData;
      }
    } catch (error) {
      console.error("\u54CD\u5E94\u6570\u636E\u89E3\u5BC6\u5931\u8D25:", error);
      throw error;
    }
  }
  /**
   * 加密请求数据
   * @param data 要加密的数据
   * @returns 加密后的数据
   */
  encryptRequest(data) {
    if (!this.config.enable) {
      return data;
    }
    try {
      const jsonData = typeof data === "string" ? data : JSON.stringify(data);
      if (this.config.algorithm.toUpperCase() === "AES") {
        if (!this.config.requestKey) {
          throw new Error("AES \u8BF7\u6C42\u52A0\u5BC6\u5BC6\u94A5\u672A\u914D\u7F6E");
        }
        return AES.encrypt(jsonData, this.config.requestKey);
      } else if (this.config.algorithm.toUpperCase() === "RSA") {
        if (!this.config.requestKey) {
          throw new Error("RSA \u516C\u94A5\u672A\u914D\u7F6E");
        }
        const result = RSA.encrypt(jsonData, this.config.requestKey);
        if (result === false) {
          throw new Error("RSA \u52A0\u5BC6\u5931\u8D25");
        }
        return result;
      } else {
        throw new Error(`\u4E0D\u652F\u6301\u7684\u52A0\u5BC6\u7B97\u6CD5: ${this.config.algorithm}`);
      }
    } catch (error) {
      console.error("\u8BF7\u6C42\u6570\u636E\u52A0\u5BC6\u5931\u8D25:", error);
      throw error;
    }
  }
  /**
   * 获取加密头名称
   */
  getEncryptHeader() {
    return this.config.header;
  }
}
function createApiEncrypt(env) {
  const config = {
    enable: env.VITE_APP_API_ENCRYPT_ENABLE === "true",
    header: env.VITE_APP_API_ENCRYPT_HEADER || "X-Api-Encrypt",
    algorithm: env.VITE_APP_API_ENCRYPT_ALGORITHM || "AES",
    requestKey: env.VITE_APP_API_ENCRYPT_REQUEST_KEY || "",
    responseKey: env.VITE_APP_API_ENCRYPT_RESPONSE_KEY || ""
  };
  return new ApiEncrypt(config);
}

function isUndefined(value) {
  return value === void 0;
}
function isBoolean(value) {
  return typeof value === "boolean";
}
function isEmpty(value) {
  if (value === null || value === void 0) {
    return true;
  }
  if (Array.isArray(value) || isString(value)) {
    return value.length === 0;
  }
  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }
  if (isObject(value)) {
    return Object.keys(value).length === 0;
  }
  return false;
}
function isHttpUrl(url) {
  if (!url) {
    return false;
  }
  const httpRegex = /^https?:\/\/.*$/;
  return httpRegex.test(url);
}
function isWindow(value) {
  return typeof window !== "undefined" && value !== null && value === value.window;
}
function isMacOs() {
  const macRegex = /macintosh|mac os x/i;
  return macRegex.test(navigator.userAgent);
}
function isWindowsOs() {
  const windowsRegex = /windows|win32/i;
  return windowsRegex.test(navigator.userAgent);
}
function isNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function getFirstNonNullOrUndefined(...values) {
  for (const value of values) {
    if (value !== void 0 && value !== null) {
      return value;
    }
  }
  return void 0;
}

function formatToFractionDigit(num, digit = 2) {
  if (isUndefined(num)) return "0.00";
  const parsedNumber = isString(num) ? Number.parseFloat(num) : num;
  return (parsedNumber / 100).toFixed(digit);
}
function formatToFraction(num) {
  return formatToFractionDigit(num, 2);
}
function floatToFixed2(num) {
  let str = "0.00";
  if (isUndefined(num)) return str;
  const f = formatToFraction(num);
  const decimalPart = f.toString().split(".")[1];
  const len = decimalPart ? decimalPart.length : 0;
  switch (len) {
    case 0: {
      str = `${f.toString()}.00`;
      break;
    }
    case 1: {
      str = `${f.toString()}0`;
      break;
    }
    case 2: {
      str = f.toString();
      break;
    }
  }
  return str;
}
function convertToInteger(num) {
  if (isUndefined(num)) return 0;
  const parsedNumber = isString(num) ? Number.parseFloat(num) : num;
  return Math.round(parsedNumber * 100);
}
function yuanToFen(amount) {
  return convertToInteger(amount);
}
function fenToYuan(price) {
  return formatToFraction(price);
}
const fenToYuanFormat = (_, __, cellValue, ___) => {
  return `\uFFE5${floatToFixed2(cellValue)}`;
};
function calculateRelativeRate(value, reference) {
  if (!reference || reference === 0) return 0;
  return Number.parseFloat(
    (100 * ((value || 0) - reference) / reference).toFixed(0)
  );
}
const ERP_COUNT_DIGIT = 3;
const ERP_PRICE_DIGIT = 2;
function erpNumberFormatter(num, digit) {
  if (num === null || num === void 0) {
    return "";
  }
  if (typeof num === "string") {
    num = Number.parseFloat(num);
  }
  if (Number.isNaN(num)) {
    return "";
  }
  return num.toFixed(digit);
}
function erpCountInputFormatter(num) {
  return erpNumberFormatter(num, ERP_COUNT_DIGIT);
}
function erpCountTableColumnFormatter(cellValue) {
  return erpNumberFormatter(cellValue, ERP_COUNT_DIGIT);
}
function erpPriceInputFormatter(num) {
  return erpNumberFormatter(num, ERP_PRICE_DIGIT);
}
function erpPriceTableColumnFormatter(cellValue) {
  return erpNumberFormatter(cellValue, ERP_PRICE_DIGIT);
}
function erpPriceMultiply(price, count) {
  if (isEmpty(price) || isEmpty(count)) return void 0;
  return Number.parseFloat((price * count).toFixed(ERP_PRICE_DIGIT));
}
function erpCalculatePercentage(value, total) {
  if (total === 0) return 0;
  return (value / total * 100).toFixed(2);
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
function toLowerCaseFirstLetter(str) {
  if (!str) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
}
function toCamelCase(key, parentKey) {
  if (!parentKey) {
    return key;
  }
  return parentKey + key.charAt(0).toUpperCase() + key.slice(1);
}
function kebabToCamelCase(str) {
  return str.split("-").filter(Boolean).map(
    (word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
  ).join("");
}

const mergeWithArrayOverride = createDefu((originObj, key, updates) => {
  if (Array.isArray(originObj[key]) && Array.isArray(updates)) {
    originObj[key] = updates;
    return true;
  }
});

let nProgressInstance = null;
async function loadNprogress() {
  if (nProgressInstance) {
    return nProgressInstance;
  }
  nProgressInstance = await import('nprogress');
  nProgressInstance.configure({
    showSpinner: true,
    speed: 300
  });
  return nProgressInstance;
}
async function startProgress() {
  const nprogress = await loadNprogress();
  nprogress?.start();
}
async function stopProgress() {
  const nprogress = await loadNprogress();
  nprogress?.done();
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      return resolve();
    }
    const script = document.createElement("script");
    script.src = src;
    script.addEventListener("load", () => resolve());
    script.addEventListener(
      "error",
      () => reject(new Error(`Failed to load script: ${src}`))
    );
    document.head.append(script);
  });
}

class StateHandler {
  condition = false;
  rejectCondition = null;
  resolveCondition = null;
  isConditionTrue() {
    return this.condition;
  }
  reset() {
    this.condition = false;
    this.clearPromises();
  }
  // 触发状态为 false 时，reject
  setConditionFalse() {
    this.condition = false;
    if (this.rejectCondition) {
      this.rejectCondition();
      this.clearPromises();
    }
  }
  // 触发状态为 true 时，resolve
  setConditionTrue() {
    this.condition = true;
    if (this.resolveCondition) {
      this.resolveCondition();
      this.clearPromises();
    }
  }
  // 返回一个 Promise，等待 condition 变为 true
  waitForCondition() {
    return new Promise((resolve, reject) => {
      if (this.condition) {
        resolve();
      } else {
        this.resolveCondition = resolve;
        this.rejectCondition = reject;
      }
    });
  }
  // 清理 resolve/reject 函数
  clearPromises() {
    this.resolveCondition = null;
    this.rejectCondition = null;
  }
}

function formatTime(time, fmt) {
  if (time) {
    const date = new Date(time);
    const o = {
      "M+": date.getMonth() + 1,
      "d+": date.getDate(),
      "H+": date.getHours(),
      "m+": date.getMinutes(),
      "s+": date.getSeconds(),
      "q+": Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    };
    const yearMatch = fmt.match(/y+/);
    if (yearMatch) {
      fmt = fmt.replace(
        yearMatch[0],
        `${date.getFullYear()}`.slice(4 - yearMatch[0].length)
      );
    }
    for (const k in o) {
      const match = fmt.match(new RegExp(`(${k})`));
      if (match) {
        fmt = fmt.replace(
          match[0],
          match[0].length === 1 ? o[k] : `00${o[k]}`.slice(
            `${o[k]}`.length
          )
        );
      }
    }
    return fmt;
  } else {
    return "";
  }
}
function getWeek(dateTime) {
  const temptTime = new Date(dateTime);
  const weekday = temptTime.getDay() || 7;
  temptTime.setDate(temptTime.getDate() - weekday + 1 + 5);
  let firstDay = new Date(temptTime.getFullYear(), 0, 1);
  const dayOfWeek = firstDay.getDay();
  let spendDay = 1;
  if (dayOfWeek !== 0) spendDay = 7 - dayOfWeek + 1;
  firstDay = new Date(temptTime.getFullYear(), 0, 1 + spendDay);
  const d = Math.ceil((temptTime.valueOf() - firstDay.valueOf()) / 864e5);
  return Math.ceil(d / 7);
}
function formatPast(param, format = "YYYY-MM-DD HH:mm:ss") {
  let s, t;
  let time = Date.now();
  typeof param === "string" || typeof param === "object" ? t = new Date(param).getTime() : t = param;
  time = Number.parseInt(`${time - t}`);
  if (time < 1e4) {
    return "\u521A\u521A";
  } else if (time < 6e4 && time >= 1e4) {
    s = Math.floor(time / 1e3);
    return `${s}\u79D2\u524D`;
  } else if (time < 36e5 && time >= 6e4) {
    s = Math.floor(time / 6e4);
    return `${s}\u5206\u949F\u524D`;
  } else if (time < 864e5 && time >= 36e5) {
    s = Math.floor(time / 36e5);
    return `${s}\u5C0F\u65F6\u524D`;
  } else if (time < 2592e5 && time >= 864e5) {
    s = Math.floor(time / 864e5);
    return `${s}\u5929\u524D`;
  } else {
    const date = typeof param === "string" || typeof param === "object" ? new Date(param) : param;
    return formatDate(date, format);
  }
}
function formatAxis(param) {
  const hour = new Date(param).getHours();
  if (hour < 6) return "\u51CC\u6668\u597D";
  else if (hour < 9) return "\u65E9\u4E0A\u597D";
  else if (hour < 12) return "\u4E0A\u5348\u597D";
  else if (hour < 14) return "\u4E2D\u5348\u597D";
  else if (hour < 17) return "\u4E0B\u5348\u597D";
  else if (hour < 19) return "\u508D\u665A\u597D";
  else if (hour < 22) return "\u665A\u4E0A\u597D";
  else return "\u591C\u91CC\u597D";
}
function formatPast2(ms) {
  const day = Math.floor(ms / (24 * 60 * 60 * 1e3));
  const hour = Math.floor(ms / (60 * 60 * 1e3) - day * 24);
  const minute = Math.floor(ms / (60 * 1e3) - day * 24 * 60 - hour * 60);
  const second = Math.floor(
    ms / 1e3 - day * 24 * 60 * 60 - hour * 60 * 60 - minute * 60
  );
  if (day > 0) {
    return `${day} \u5929${hour} \u5C0F\u65F6 ${minute} \u5206\u949F`;
  }
  if (hour > 0) {
    return `${hour} \u5C0F\u65F6 ${minute} \u5206\u949F`;
  }
  if (minute > 0) {
    return `${minute} \u5206\u949F`;
  }
  return second > 0 ? `${second} \u79D2` : `${0} \u79D2`;
}
function beginOfDay(param) {
  return new Date(
    param.getFullYear(),
    param.getMonth(),
    param.getDate(),
    0,
    0,
    0
  );
}
function endOfDay(param) {
  return new Date(
    param.getFullYear(),
    param.getMonth(),
    param.getDate(),
    23,
    59,
    59
  );
}
function betweenDay(param1, param2) {
  param1 = convertDate(param1);
  param2 = convertDate(param2);
  return Math.floor((param2.getTime() - param1.getTime()) / (24 * 3600 * 1e3));
}
function addTime(param1, param2) {
  param1 = convertDate(param1);
  return new Date(param1.getTime() + param2);
}
function convertDate(param) {
  if (typeof param === "string") {
    return new Date(param);
  }
  return param;
}
function isSameDay(a, b) {
  if (!a || !b) return false;
  const aa = dayjs(a);
  const bb = dayjs(b);
  return aa.year() === bb.year() && aa.month() === bb.month() && aa.day() === bb.day();
}
function getDayRange(date, days) {
  const day = dayjs(date).add(days, "d");
  return getDateRange(day, day);
}
function getLast7Days() {
  const lastWeekDay = dayjs().subtract(7, "d");
  const yesterday = dayjs().subtract(1, "d");
  return getDateRange(lastWeekDay, yesterday);
}
function getLast30Days() {
  const lastMonthDay = dayjs().subtract(30, "d");
  const yesterday = dayjs().subtract(1, "d");
  return getDateRange(lastMonthDay, yesterday);
}
function getLast1Year() {
  const lastYearDay = dayjs().subtract(1, "y");
  const yesterday = dayjs().subtract(1, "d");
  return getDateRange(lastYearDay, yesterday);
}
function getDateRange(beginDate, endDate) {
  return [
    dayjs(beginDate).startOf("d").format("YYYY-MM-DD HH:mm:ss"),
    dayjs(endDate).endOf("d").format("YYYY-MM-DD HH:mm:ss")
  ];
}

async function to(promise, errorExt) {
  try {
    const data = await promise;
    const result = [null, data];
    return result;
  } catch (error) {
    if (errorExt) {
      const parsedError = Object.assign({}, error, errorExt);
      return [parsedError, void 0];
    }
    return [error, void 0];
  }
}

function traverseTreeValues(tree, getValue, options) {
  const result = [];
  const { childProps } = options || {
    childProps: "children"
  };
  const dfs = (treeNode) => {
    const value = getValue(treeNode);
    result.push(value);
    const children = treeNode?.[childProps];
    if (!children) {
      return;
    }
    if (children.length > 0) {
      for (const child of children) {
        dfs(child);
      }
    }
  };
  for (const treeNode of tree) {
    dfs(treeNode);
  }
  return result.filter(Boolean);
}
function filterTree(tree, filter, options) {
  const { childProps } = options || {
    childProps: "children"
  };
  const _filterTree = (nodes) => {
    return nodes.filter((node) => {
      if (filter(node)) {
        if (node[childProps]) {
          node[childProps] = _filterTree(node[childProps]);
        }
        return true;
      }
      return false;
    });
  };
  return _filterTree(tree);
}
function mapTree(tree, mapper, options) {
  const { childProps } = options || {
    childProps: "children"
  };
  return tree.map((node) => {
    const mapperNode = mapper(node);
    if (mapperNode[childProps]) {
      mapperNode[childProps] = mapTree(mapperNode[childProps], mapper, options);
    }
    return mapperNode;
  });
}
function handleTree(data, id = "id", parentId = "parentId", children = "children") {
  if (!Array.isArray(data)) {
    console.warn("data must be an array");
    return [];
  }
  const config = {
    id,
    parentId,
    childrenList: children
  };
  const childrenListMap = {};
  const nodeIds = {};
  const tree = [];
  for (const d of data) {
    const pId = d[config.parentId];
    if (childrenListMap[pId] === void 0) {
      childrenListMap[pId] = [];
    }
    nodeIds[d[config.id]] = d;
    childrenListMap[pId].push(d);
  }
  for (const d of data) {
    const pId = d[config.parentId];
    if (nodeIds[pId] === void 0) {
      tree.push(d);
    }
  }
  const adaptToChildrenList = (node) => {
    const nodeId = node[config.id];
    if (childrenListMap[nodeId]) {
      node[config.childrenList] = childrenListMap[nodeId];
      for (const child of node[config.childrenList]) {
        adaptToChildrenList(child);
      }
    }
  };
  for (const rootNode of tree) {
    adaptToChildrenList(rootNode);
  }
  return tree;
}
function treeToString(tree, nodeId) {
  if (tree === void 0 || !Array.isArray(tree) || tree.length === 0) {
    console.warn("tree must be an array");
    return "";
  }
  const node = tree.find((item) => item.id === nodeId);
  if (node !== void 0) {
    return node.name;
  }
  let str = "";
  function performAThoroughValidation(arr) {
    if (arr === void 0 || !Array.isArray(arr) || arr.length === 0) {
      return false;
    }
    for (const item of arr) {
      if (item.id === nodeId) {
        str += ` / ${item.name}`;
        return true;
      } else if (item.children !== void 0 && item.children.length > 0) {
        str += ` / ${item.name}`;
        if (performAThoroughValidation(item.children)) {
          return true;
        }
      }
    }
    return false;
  }
  for (const item of tree) {
    str = `${item.name}`;
    if (performAThoroughValidation(item.children)) {
      break;
    }
  }
  return str;
}

function uniqueByField(arr, key) {
  const seen = /* @__PURE__ */ new Map();
  return arr.filter((item) => {
    const value = item[key];
    return seen.has(value) ? false : (seen.set(value, item), true);
  });
}

function updateCSSVariables(variables, id = "__vben-styles__") {
  const styleElement = document.querySelector(`#${id}`) || document.createElement("style");
  styleElement.id = id;
  let cssText = ":root {";
  for (const key in variables) {
    if (Object.prototype.hasOwnProperty.call(variables, key)) {
      cssText += `${key}: ${variables[key]};`;
    }
  }
  cssText += "}";
  styleElement.textContent = cssText;
  if (!document.querySelector(`#${id}`)) {
    setTimeout(() => {
      document.head.append(styleElement);
    });
  }
}

function generateAcceptedFileTypes(supportedFileTypes) {
  const allowedExtensions = supportedFileTypes.map((ext) => ext.toLowerCase());
  const mimeTypes = [];
  if (allowedExtensions.includes("txt")) {
    mimeTypes.push("text/plain");
  }
  if (allowedExtensions.includes("pdf")) {
    mimeTypes.push("application/pdf");
  }
  if (allowedExtensions.includes("html") || allowedExtensions.includes("htm")) {
    mimeTypes.push("text/html");
  }
  if (allowedExtensions.includes("csv")) {
    mimeTypes.push("text/csv");
  }
  if (allowedExtensions.includes("xlsx") || allowedExtensions.includes("xls")) {
    mimeTypes.push(
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
  }
  if (allowedExtensions.includes("docx") || allowedExtensions.includes("doc")) {
    mimeTypes.push(
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    );
  }
  if (allowedExtensions.includes("pptx") || allowedExtensions.includes("ppt")) {
    mimeTypes.push(
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    );
  }
  if (allowedExtensions.includes("xml")) {
    mimeTypes.push("application/xml", "text/xml");
  }
  if (allowedExtensions.includes("md") || allowedExtensions.includes("markdown")) {
    mimeTypes.push("text/markdown");
  }
  if (allowedExtensions.includes("epub")) {
    mimeTypes.push("application/epub+zip");
  }
  if (allowedExtensions.includes("eml")) {
    mimeTypes.push("message/rfc822");
  }
  if (allowedExtensions.includes("msg")) {
    mimeTypes.push("application/vnd.ms-outlook");
  }
  const extensions = allowedExtensions.map((ext) => `.${ext}`);
  return [...mimeTypes, ...extensions].join(",");
}

function bindMethods(instance) {
  const prototype = Object.getPrototypeOf(instance);
  const propertyNames = Object.getOwnPropertyNames(prototype);
  propertyNames.forEach((propertyName) => {
    const descriptor = Object.getOwnPropertyDescriptor(prototype, propertyName);
    const propertyValue = instance[propertyName];
    if (typeof propertyValue === "function" && propertyName !== "constructor" && descriptor && !descriptor.get && !descriptor.set) {
      instance[propertyName] = propertyValue.bind(instance);
    }
  });
}
function getNestedValue(obj, path) {
  if (typeof path !== "string" || path.length === 0) {
    throw new Error("Path must be a non-empty string");
  }
  const keys = path.split(".");
  let current = obj;
  for (const key of keys) {
    if (current === null || current === void 0) {
      return void 0;
    }
    current = current[key];
  }
  return current;
}
function getUrlNumberValue(key, urlStr = location.href) {
  return Number(getUrlValue(key, urlStr));
}
function getUrlValue(key, urlStr = location.href) {
  if (!urlStr || !key) return "";
  const url = new URL(decodeURIComponent(urlStr));
  return url.searchParams.get(key) ?? "";
}
function copyValueToTarget(target, source) {
  const newObj = Object.assign({}, target, source);
  Object.keys(newObj).forEach((key) => {
    if (!Object.keys(target).includes(key)) {
      delete newObj[key];
    }
  });
  Object.assign(target, newObj);
}
function groupBy(array, key) {
  const result = {};
  for (const item of array) {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
  }
  return result;
}

const hexList = [];
for (let i = 0; i <= 15; i++) {
  hexList[i] = i.toString(16);
}
function buildUUID() {
  let uuid = "";
  for (let i = 1; i <= 36; i++) {
    switch (i) {
      case 9:
      case 14:
      case 19:
      case 24: {
        uuid += "-";
        break;
      }
      case 15: {
        uuid += 4;
        break;
      }
      case 20: {
        uuid += hexList[Math.random() * 4 | 8];
        break;
      }
      default: {
        uuid += hexList[Math.trunc(Math.random() * 16)];
      }
    }
  }
  return uuid.replaceAll("-", "");
}
let unique = 0;
function buildShortUUID(prefix = "") {
  const time = Date.now();
  const random = Math.floor(Math.random() * 1e9);
  unique++;
  return `${prefix}_${random}${unique}${String(time)}`;
}

export { AES, ApiEncrypt, RSA, StateHandler, addTime, arraysEqual, base64ToFile, beginOfDay, betweenDay, bindMethods, buildShortUUID, buildUUID, calculateRelativeRate, capitalizeFirstLetter, cn, convertDate, convertToInteger, copyValueToTarget, createApiEncrypt, dataURLtoBlob, dateFormatter, diff, downloadFileFromBase64, downloadFileFromBlob, downloadFileFromBlobPart, downloadFileFromImageUrl, downloadFileFromUrl, downloadImageByCanvas, endOfDay, erpCalculatePercentage, erpCountInputFormatter, erpCountTableColumnFormatter, erpNumberFormatter, erpPriceInputFormatter, erpPriceMultiply, erpPriceTableColumnFormatter, fenToYuan, fenToYuanFormat, filterTree, floatToFixed2, formatAxis, formatDate, formatDate2, formatDateTime, formatPast, formatPast2, formatTime, formatToFraction, formatToFractionDigit, generateAcceptedFileTypes, getDateRange, getDayRange, getElementVisibleRect, getFirstNonNullOrUndefined, getLast1Year, getLast30Days, getLast7Days, getNestedValue, getScrollbarWidth, getUrlNumberValue, getUrlValue, getWeek, groupBy, handleTree, isBoolean, isDate, isDayjsObject, isEmpty, isHttpUrl, isMacOs, isNumber, isSameDay, isUndefined, isWindow, isWindowsOs, kebabToCamelCase, loadScript, mapTree, mergeWithArrayOverride, needsScrollbar, openRouteInNewWindow, openWindow, startProgress, stopProgress, to, toCamelCase, toLowerCaseFirstLetter, traverseTreeValues, treeToString, triggerDownload, triggerWindowResize, uniqueByField, updateCSSVariables, urlToBase64, yuanToFen };
